import { inject } from 'vue';
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import { DEFAULT_LOGIN_REDIRECT_ROUTE_NAME, useSessionStore } from "@/stores/session";
import { useAppStore } from "@/stores/app";
import type { VueCookies } from "vue-cookies";
import AppLayout from "@/layouts/AppLayout.vue";
import FullPageLayout from "@/layouts/FullPageLayout.vue";
import { useCardsStore } from "@/stores/cards";
import { MembershipStatus } from "@/models/membership";
import { UserPermissionFlag } from "@/models/user";
import { MembershipTierPlanFrequency } from "@/models/membershipTierPlan";

function checkTierSelected(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  const cookies = inject<VueCookies>('$cookies')!;

  if (!cookies.get('changeit_signup_tier_id')) {
    return {name: 'sign-up-tiers', query: to.query};
  }
}

async function checkAmexOnlyCards(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  const appStore = useAppStore();
  const cardsStore = useCardsStore();
  if (appStore.isAmexUxLimited && cardsStore.hasOnlyAmexCards) {
    return {name: 'home', query: to.query};
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'login',
          component: () => import('../views/session/LoginView.vue'),
          meta: {
            title: 'Login',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/activate',
      name: 'activate-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'activate',
          component: () => import('../views/session/ActivateView.vue'),
          meta: {
            title: 'Activate',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/sign-up',
      name: 'sign-up-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'sign-up',
          component: () => import('../views/session/SignUpView.vue'),
          meta: {
            title: 'Sign Up',
            requiresAuth: false,
          },
          beforeEnter: (to, from) => {
            const appStore = useAppStore();
            const cookies = inject<VueCookies>('$cookies')!;

            // Create cookies
            const newReferralCodeParam = to.query.referralcode as string | undefined;
            const referralCodeParam = to.query.REFERRALCODE as string | undefined;
            if (newReferralCodeParam || referralCodeParam) {
              cookies.set('changeit_signup_referral_code', newReferralCodeParam ?? referralCodeParam, '1h');
              delete to.query.referralcode;
              delete to.query.REFERRALCODE;
            }

            const membershipTierNameParam = to.query.tier as string | undefined;
            if (membershipTierNameParam) {
              const membershipTier = appStore.membershipTiers.find((tier) => tier.name.toLowerCase() === membershipTierNameParam.toLowerCase());
              if (membershipTier) {
                cookies.set('changeit_signup_tier_id', membershipTier.id, '1h');
              }
              delete to.query.tier;

              const membershipTierPlanParam = to.query.plan as string | undefined;
              const membershipTierPlan = membershipTier?.membershipTierPlans
                .find((plan) => plan.frequency === (membershipTierPlanParam?.toLowerCase() ?? MembershipTierPlanFrequency.MONTH));
              cookies.set('changeit_signup_tier_plan_id', membershipTierPlan?.id, '1h');
              delete to.query.plan;
            }

            const maxRoundUp = to.query.maxRoundUp as string | undefined;
            if (maxRoundUp) {
              cookies.set('changeit_signup_max_round_up', maxRoundUp, '1h');
              delete to.query.maxRoundUp;
            }

            let affiliateProgram: string | undefined;
            const affiliateProgramQuery = to.query.affid as string[] | string | undefined;
            if (Array.isArray(affiliateProgramQuery)) {
              const cleaned = affiliateProgramQuery.filter((value) => value && value !== 'null');
              if (cleaned.length > 0) {
                affiliateProgram = cleaned[ 0 ];
              }
            } else {
              affiliateProgram = affiliateProgramQuery;
            }
            if (affiliateProgram) {
              cookies.set('changeit_signup_affiliate_program', affiliateProgram, '1h');
              delete to.query.affid;
            }

            const destinationCodeParam = to.query.dest as string | undefined;
            if (destinationCodeParam) {
              cookies.remove('changeit_signup_destination_code');
              if (appStore.client!.featureFlags.destinations) {
                const destination = appStore.destinations?.find((d) => d.code === destinationCodeParam);
                if (destination) {
                  cookies.set('changeit_signup_destination_code', destination.code, '1h');
                }
              }
              delete to.query.dest;
            }

            // Boot out of the flow if using the new billing system and tier is not selected
            if (
              !cookies.get('changeit_signup_tier_id')
              && !cookies.get('change_it_api_session')
              && appStore.client!.landingPageUrl
            ) {
              window.location.href = appStore.client!.landingPageUrl;
              return false;
            }

            // Redirect if this was the destination route
            if (to.name === 'sign-up') {
              if (!appStore.isSubscriptionProgram) {
                return {name: 'sign-up-account', query: to.query};
              } else {
                return {name: 'sign-up-tiers', query: to.query};
              }
            }
          },
          children: [
            {
              path: 'tiers',
              name: 'sign-up-tiers',
              component: () => import('../views/session/signup/SignUpTiersView.vue'),
              meta: {
                title: 'Pick Tier',
                requiresAuth: false,
              },
              beforeEnter: [
                (to, from) => {
                  const appStore = useAppStore();
                  const sessionStore = useSessionStore();
                  const cookies = inject<VueCookies>('$cookies')!;

                  if (cookies.get('changeit_signup_tier_id')) {
                    const tierId = parseInt(cookies.get('changeit_signup_tier_id')!);
                    if (!isNaN(tierId) && appStore.membershipTiers.find((tier) => tier.id === tierId)) {
                      return {name: 'sign-up-account', query: to.query};
                    }
                  }

                  // If they have a membership tier assigned, but no subscription, redirect to renewal
                  // If they are redirected to renewal, but have no card, they will be redirected to billing
                  if (!!sessionStore.membership && !sessionStore.membership!.currentSubscription && !!sessionStore.membership!.membershipTierId) {

                    // Get tier plan id
                    const tier = appStore.membershipTiers.find((tier) => tier.id === sessionStore.membership!.membershipTierId);
                    if (tier) {
                      cookies.set('changeit_signup_tier_id', tier.id, '10min');

                      if (tier.membershipTierPlans.length > 0) {
                        cookies.set('changeit_signup_tier_plan_id', tier.membershipTierPlans[ 0 ].id, '10min');
                      }
                    }

                    return {name: 'renew', query: to.query};
                  }
                },
              ],
            },
            {
              path: 'account',
              name: 'sign-up-account',
              component: () => import('../views/session/signup/SignUpAccountView.vue'),
              meta: {
                title: 'Account',
                requiresAuth: false,
              },
              beforeEnter: [
                checkTierSelected,
                (to, from) => {
                  const sessionStore = useSessionStore();
                  if (sessionStore.isLogged && sessionStore.isMember) {
                    return {name: 'sign-up-billing', query: to.query};
                  }
                },
              ],
            },
            {
              path: 'billing',
              name: 'sign-up-billing',
              component: () => import('../views/session/signup/SignUpBillingView.vue'),
              meta: {
                title: 'Checkout',
                requiresAuth: false,
              },
              beforeEnter: [
                checkTierSelected,
                async (to, from) => {
                  const cardsStore = useCardsStore();
                  const sessionStore = useSessionStore();

                  if (cardsStore.cards.length > 0 && sessionStore.membership!.membershipTierId) {
                    return {name: 'home', query: to.query};
                  }
                },
              ],
            },
            {
              path: 'affinity',
              name: 'sign-up-affinity',
              component: () => import('../views/session/signup/SignUpAffinityView.vue'),
              meta: {
                title: 'Select Affinity',
                requiresAuth: false,
              },
              beforeEnter: [
                async (to, from) => {
                  const cardsStore = useCardsStore();
                  if (cardsStore.cards.length === 0) {
                    return {name: 'sign-up-billing', query: to.query};
                  }
                },
              ],
            },
            {
              path: 'user-info',
              name: 'sign-up-user-info',
              component: () => import('../views/session/signup/SignUpUserInfo.vue'),
              meta: {
                title: 'User Info',
                requiresAuth: false,
              },
              beforeEnter: [],
            },
          ],
        },
      ],
    },
    {
      path: '/password-forgot',
      name: 'password-forgot-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'password-forgot',
          component: () => import('../views/session/PasswordForgotView.vue'),
          meta: {
            title: 'Password Forgot',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/password-reset-1',
      name: 'password-reset-wrapper-1',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'password-reset-1',
          component: () => import('../views/session/PasswordResetView1.vue'),
          props: (route) => ({
            email: route.query.email,
          }),
          meta: {
            title: 'Password Reset Code',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/password-reset-2',
      name: 'password-reset-wrapper-2',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'password-reset-2',
          component: () => import('../views/session/PasswordResetView2.vue'),
          props: (route) => ({
            email: route.query.email,
            token: route.query.token,
          }),
          meta: {
            title: 'Password Reset',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/support/terms',
      name: 'terms-of-service-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'terms-of-service',
          component: () => import('../views/legal/TermsOfServiceView.vue'),
          meta: {
            title: 'Terms of Service',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/support/privacy',
      name: 'privacy-policy-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'privacy-policy',
          component: () => import('../views/legal/PrivacyPolicyView.vue'),
          meta: {
            title: 'Privacy Policy',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/enroll',
      name: 'enroll-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'enroll',
          component: () => import('../views/session/EnrollView.vue'),
          meta: {
            title: 'Enroll',
            requiresAuth: true,
          },
        },
      ],
    },
    {
      path: '/renew',
      name: 'renew-wrapper',
      component: FullPageLayout,
      children: [
        {
          path: '',
          name: 'renew',
          component: () => import('../views/RenewView.vue'),
          meta: {
            title: 'Renew',
            requiresAuth: true,
          },
          beforeEnter: [
            async (to, from) => {
              const sessionStore = useSessionStore();
              if (sessionStore.membership!.status === MembershipStatus.ACTIVE || sessionStore.currentSubscription) {
                return {name: 'home', query: to.query};
              }
            },
          ],
        },
      ],
    },
    {
      path: '/',
      name: 'main',
      component: AppLayout,
      children: [
        {
          path: '',
          name: 'home',
          // route level code-splitting
          component: () => import('../views/HomeView.vue'),
          meta: {
            title: 'Home',
            requiresAuth: true,
          },
        },
        {
          path: '/rewards',
          name: 'rewards',
          // route level code-splitting
          // this generates a separate chunk (About.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import('../views/RewardsView.vue'),
          meta: {
            title: 'Rewards and Offers',
            requiresAuth: true,
            // duotoneBackground: true,
          },
          beforeEnter: checkAmexOnlyCards,
        },
        {
          path: '/refer',
          name: 'referral',
          component: () => import('../views/ReferralView.vue'),
          meta: {
            title: 'My referrals',
            requiresAuth: true,
          },
          beforeEnter: (to, from) => {
            const appStore = useAppStore();
            if (!appStore.client!.featureFlags.newReferralSystem) {
              return {name: 'home', query: to.query};
            }
          },
        },
        {
          path: '/activity',
          name: 'activity',
          component: () => import('../views/ActivityView.vue'),
          meta: {
            title: 'My activity',
            requiresAuth: true,
            duotoneBackground: true,
            duotoneBackgroundDots: true,
          },
          beforeEnter: checkAmexOnlyCards,
        },
        {
          path: '/account',
          name: 'account',
          component: () => import('../views/AccountView.vue'),
          meta: {
            title: 'My account',
            requiresAuth: true,
            duotoneBackground: true,
            duotoneBackgroundDots: true,
          },
        },
        {
          path: '/videos',
          name: 'videos',
          component: () => import('../views/VideosView.vue'),
          meta: {
            title: 'Videos',
            requiresAuth: true,
            duotoneBackground: true,
            duotoneBackgroundDots: true,
          },
          beforeEnter: (to, from) => {
            const sessionStore = useSessionStore();
            if (!sessionStore.canSeeVideosSection) {
              return {name: 'home', query: to.query};
            }
          },
        },
        // {
        //   path: '/test',
        //   name: 'ui-test',
        //   // route level code-splitting
        //   component: () => import('../views/TestView.vue'),
        //   meta: {
        //     title: 'UI Test',
        //     requiresAuth: true,
        //   },
        // },
      ],
    },
    {
      path: '/:patchMatch(.*)',
      name: 'not-found',
      redirect: {name: 'home'},
    },
  ],
});

router.beforeEach(async (to, from) => {
  const sessionStore = useSessionStore();
  await sessionStore.init();
  if (to.name === 'login') {
    if (sessionStore.isLogged) {
      return {name: DEFAULT_LOGIN_REDIRECT_ROUTE_NAME, query: to.query};
    }
  } else if (to.name === 'sign-up') {
    if (sessionStore.isLogged) {
      return {name: DEFAULT_LOGIN_REDIRECT_ROUTE_NAME, query: to.query};
    }
  } else if (to.name === 'enroll') {
    if (sessionStore.isMember) {
      return {name: DEFAULT_LOGIN_REDIRECT_ROUTE_NAME, query: to.query};
    }
  } else {
    if (to.meta.requiresAuth) {

      if (!sessionStore.isLogged) {
        sessionStore.initLogin(to.name);
        return {name: 'login', query: to.query};
      }

      if (!sessionStore.isMember) {
        return {name: 'enroll', query: to.query};
      }

      if (sessionStore.loggedUser?.permissionFlag !== UserPermissionFlag.ADMIN) {
        if ((sessionStore.membership!.status === MembershipStatus.ABANDONED_CHECKOUT || (sessionStore.membership!.status === MembershipStatus.CHURNED && !sessionStore.isActiveChurned)) && to.name !== 'sign-up-billing') {
          return {name: 'sign-up-billing', query: to.query};
        }
        if (sessionStore.membership!.status === MembershipStatus.SUSPENDED && to.name !== 'renew') {
          return {name: 'renew', query: to.query};
        }
        if (sessionStore.isActiveChurned && to.name !== 'account') {
          return {name: 'account', query: to.query};
        }
      }
    }
  }
});

router.afterEach((to) => {
  const appStore = useAppStore();

  if (appStore.isSubscriptionProgram || appStore.isWhiteLabel) {
    document.title = to.meta.title;
  } else {
    document.title = [to.meta.title, import.meta.env.VITE_APP_NAME].join(' | ');
  }
  appStore.showDuotoneBackground = to.meta.duotoneBackground ?? false;
  appStore.showDuotoneBackgroundDots = to.meta.duotoneBackgroundDots ?? false;
});

export default router;

declare module 'vue-router' {
  interface RouteMeta {
    title: string;
    requiresAuth: boolean;
    duotoneBackground?: boolean;
    duotoneBackgroundDots?: boolean;
  }
}
